// ============================================================================
// Facility picker — searchable dropdown of facilities visible to the user
// (RLS handles scoping). Designed for the supply-request flow: small,
// keyboard-driven, returns the full Facility row so the caller can also
// derive account_id and ship_to_address.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../shared/supabase';
import type { Facility } from '../../shared/types';

interface Props {
  value: string | null; // selected facility_id
  onChange: (facility: Facility | null) => void;
  // Pre-filter to facilities owned by this AM. Useful in the AM voice-flow
  // where the parser shouldn't have to consider the full 182-facility list.
  scopeToEmployee?: string | null;
  // Default (false) lists only operational (active) facilities via the
  // active_facilities view. Set true to search the full facilities table
  // including Closed — e.g. relinking a historical project on the Project
  // detail screen, where the target facility may already be closed.
  includeClosed?: boolean;
  // Optional placeholder override for the search input.
  placeholder?: string;
}

export default function FacilityPicker({
  value,
  onChange,
  scopeToEmployee = null,
  includeClosed = false,
  placeholder,
}: Props) {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // 2026-05-12 repoint: supply picker is operational — no closed
      // facilities. Repointed to active_facilities view.
      // 2026-07-20: includeClosed=true searches the full facilities table
      // (incl. Closed). The base table has no account_manager_name column
      // (view-only), so that column is dropped in the closed-inclusive query —
      // the picker never displays it, and callers derive the account/AM
      // separately from account_id.
      const { data, error: e } = includeClosed
        ? await (() => {
            let q = supabase
              .from('facilities')
              .select(
                'facility_id, facility_name, account_id, facility_type, facility_address, state, status, market_region, account_manager_eid, monthly_rate, account_status_rag, rag_set_by, rag_set_at, rag_reason, rag_category, latitude, longitude',
              )
              .order('facility_name');
            if (scopeToEmployee) q = q.eq('account_manager_eid', scopeToEmployee);
            return q;
          })()
        : await (() => {
            let q = supabase
              .from('active_facilities')
              .select(
                // Trim columns vs Account Mgmt — picker only needs id/name/address/AM.
                // 2026-05-19: denorm counter cols (last_cleaning_date, last_feedback_date,
                // open_task_count, open_feedback_count) dropped from facilities.
                'facility_id, facility_name, account_id, facility_type, facility_address, state, status, market_region, account_manager_eid, account_manager_name, monthly_rate, account_status_rag, rag_set_by, rag_set_at, rag_reason, rag_category, latitude, longitude',
              )
              .order('facility_name');
            if (scopeToEmployee) q = q.eq('account_manager_eid', scopeToEmployee);
            return q;
          })();
      if (cancelled) return;
      if (e) {
        setError(e.message);
      } else {
        setFacilities((data ?? []) as unknown as Facility[]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeToEmployee, includeClosed]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return facilities;
    return facilities.filter((f) => {
      const hay = [f.facility_name, f.facility_address, f.market_region]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [facilities, search]);

  const selected = useMemo(
    () => facilities.find((f) => f.facility_id === value) ?? null,
    [facilities, value],
  );

  return (
    <div>
      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-st-ok-border bg-st-ok-bg px-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-tcs-text">
              {selected.facility_name}
            </div>
            <div className="truncate text-xs text-tcs-text-muted">
              {selected.facility_id}
              {selected.facility_address ? ` · ${selected.facility_address}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="ml-2 shrink-0 rounded border border-tcs-border-strong bg-tcs-raised px-2 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={placeholder ?? 'Search facility name, address, market…'}
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none"
          />
          {loading && (
            <p className="mt-2 text-xs text-tcs-text-muted">Loading facilities…</p>
          )}
          {error && <p className="mt-2 text-xs text-rag-red">{error}</p>}

          {!loading && !error && (
            <ul className="mt-2 max-h-72 overflow-y-auto rounded-md border border-tcs-border bg-tcs-raised">
              {filtered.slice(0, 200).map((f) => (
                <li key={f.facility_id}>
                  <button
                    type="button"
                    onClick={() => onChange(f)}
                    className="flex w-full items-center justify-between gap-2 border-b border-tcs-border px-3 py-2 text-left text-sm hover:bg-tcs-bg-muted"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-tcs-text">
                        {f.facility_name}
                      </div>
                      <div className="truncate text-xs text-tcs-text-muted">
                        {f.facility_id}
                        {f.market_region ? ` · ${f.market_region}` : ''}
                        {f.status && f.status !== 'Active' ? ` · ${f.status}` : ''}
                      </div>
                    </div>
                    {f.facility_type && (
                      <span className="shrink-0 rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-medium uppercase text-tcs-text-secondary">
                        {f.facility_type}
                      </span>
                    )}
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="p-4 text-center text-sm text-tcs-text-muted">
                  No matches.
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
